<template>
  <div class="users-view">
    <div class="users-header">
      <h2>Управление пользователями</h2>
      <button @click="showAddUserModal = true" class="add-user-button">
        <i class="fas fa-user-plus"></i> Добавить пользователя
      </button>
    </div>
    
    <div v-if="loading" class="loading">
      <i class="fas fa-spinner fa-spin"></i> Загрузка...
    </div>
    
    <div v-else-if="error" class="error-message">
      {{ error }}
    </div>
    
    <div v-else class="users-list">
      <table>
        <thead>
          <tr>
            <th>Имя</th>
            <th>Логин</th>
            <th>Роль</th>
            <th>Статус</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user._id">
            <td>{{ user.name }}</td>
            <td>{{ user.login }}</td>
            <td>{{ user.role === 'admin' ? 'Администратор' : 'Менеджер' }}</td>
            <td>
              <span :class="user.isActive ? 'status-active' : 'status-inactive'">
                {{ user.isActive ? 'Активен' : 'Неактивен' }}
              </span>
            </td>
            <td class="actions">
              <button @click="editUser(user)" class="edit-button">
                <i class="fas fa-edit"></i>
              </button>
              <button @click="confirmDeleteUser(user)" class="delete-button" :disabled="user._id === currentUser.id">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Модальное окно для добавления/редактирования пользователя -->
    <div v-if="showAddUserModal || showEditUserModal" class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ showEditUserModal ? 'Редактировать пользователя' : 'Добавить пользователя' }}</h3>
          <button @click="closeModal" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <form @submit.prevent="showEditUserModal ? updateUser() : addUser()" class="user-form">
          <div class="form-group">
            <label for="name">Имя</label>
            <input 
              type="text" 
              id="name" 
              v-model="userForm.name" 
              required 
              class="form-control"
            />
          </div>
          
          <div class="form-group">
            <label for="login">Логин</label>
            <input 
              type="text" 
              id="login" 
              v-model="userForm.login" 
              required 
              class="form-control"
            />
          </div>
          
          <div class="form-group" v-if="!showEditUserModal">
            <label for="password">Пароль</label>
            <div class="password-input">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                id="password" 
                v-model="userForm.password" 
                required 
                class="form-control"
              />
              <button 
                type="button" 
                @click="showPassword = !showPassword" 
                class="toggle-password"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
          
          <div class="form-group" v-if="showEditUserModal">
            <label for="new-password">Новый пароль (оставьте пустым, чтобы не менять)</label>
            <div class="password-input">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                id="new-password" 
                v-model="userForm.password" 
                class="form-control"
              />
              <button 
                type="button" 
                @click="showPassword = !showPassword" 
                class="toggle-password"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
          
          <div class="form-group">
            <label for="role">Роль</label>
            <select id="role" v-model="userForm.role" required class="form-control">
              <option value="manager">Менеджер</option>
              <option value="admin">Администратор</option>
            </select>
          </div>
          
          <div class="form-group" v-if="showEditUserModal">
            <label for="status">Статус</label>
            <select id="status" v-model="userForm.isActive" required class="form-control">
              <option :value="true">Активен</option>
              <option :value="false">Неактивен</option>
            </select>
          </div>
          
          <div v-if="formError" class="error-message">
            {{ formError }}
          </div>
          
          <div class="form-actions">
            <button type="button" @click="closeModal" class="cancel-button">Отмена</button>
            <button type="submit" class="submit-button" :disabled="formLoading">
              <span v-if="formLoading">
                <i class="fas fa-spinner fa-spin"></i> Сохранение...
              </span>
              <span v-else>
                {{ showEditUserModal ? 'Сохранить' : 'Добавить' }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Модальное окно для подтверждения удаления -->
    <div v-if="showDeleteModal" class="modal">
      <div class="modal-content delete-modal">
        <div class="modal-header">
          <h3>Удаление пользователя</h3>
          <button @click="showDeleteModal = false" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <p>Вы уверены, что хотите удалить пользователя "{{ deletingUser?.name }}"?</p>
          <p class="warning">Это действие нельзя отменить.</p>
        </div>
        
        <div class="modal-footer">
          <button @click="showDeleteModal = false" class="cancel-button">Отмена</button>
          <button @click="deleteUser" class="delete-confirm-button" :disabled="deleteLoading">
            <span v-if="deleteLoading">
              <i class="fas fa-spinner fa-spin"></i> Удаление...
            </span>
            <span v-else>Удалить</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

export default {
  name: 'UsersView',
  setup() {
    const store = useStore();
    const users = ref([]);
    const loading = ref(true);
    const error = ref(null);
    
    const showAddUserModal = ref(false);
    const showEditUserModal = ref(false);
    const showDeleteModal = ref(false);
    const showPassword = ref(false);
    
    const userForm = ref({
      name: '',
      login: '',
      password: '',
      role: 'manager',
      isActive: true
    });
    
    const editingUser = ref(null);
    const deletingUser = ref(null);
    
    const formLoading = ref(false);
    const formError = ref(null);
    const deleteLoading = ref(false);
    
    const currentUser = computed(() => store.state.auth.user);
    
    const fetchUsers = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await axios.get('/auth/users');
        users.value = response.data;
      } catch (err) {
        console.error('Ошибка при получении списка пользователей:', err);
        error.value = err.response?.data?.error || 'Не удалось загрузить список пользователей';
      } finally {
        loading.value = false;
      }
    };
    
    const closeModal = () => {
      showAddUserModal.value = false;
      showEditUserModal.value = false;
      editingUser.value = null;
      userForm.value = {
        name: '',
        login: '',
        password: '',
        role: 'manager',
        isActive: true
      };
      formError.value = null;
      showPassword.value = false;
    };
    
    const editUser = (user) => {
      editingUser.value = user;
      userForm.value = {
        name: user.name,
        login: user.login,
        password: '',
        role: user.role,
        isActive: user.isActive
      };
      showEditUserModal.value = true;
    };
    
    const addUser = async () => {
      formLoading.value = true;
      formError.value = null;
      
      try {
        await axios.post('/auth/users', userForm.value);
        await fetchUsers();
        closeModal();
      } catch (err) {
        console.error('Ошибка при создании пользователя:', err);
        formError.value = err.response?.data?.error || 'Не удалось создать пользователя';
      } finally {
        formLoading.value = false;
      }
    };
    
    const updateUser = async () => {
      formLoading.value = true;
      formError.value = null;
      
      try {
        const userData = { ...userForm.value };
        
        // Если пароль не указан, удаляем его из запроса
        if (!userData.password) {
          delete userData.password;
        }
        
        await axios.put(`/auth/users/${editingUser.value._id}`, userData);
        
        // Если обновляем текущего пользователя, обновляем данные в хранилище
        if (editingUser.value._id === currentUser.value.id) {
          await store.dispatch('fetchCurrentUser');
        }
        
        await fetchUsers();
        closeModal();
      } catch (err) {
        console.error('Ошибка при обновлении пользователя:', err);
        formError.value = err.response?.data?.error || 'Не удалось обновить пользователя';
      } finally {
        formLoading.value = false;
      }
    };
    
    const confirmDeleteUser = (user) => {
      deletingUser.value = user;
      showDeleteModal.value = true;
    };
    
    const deleteUser = async () => {
      deleteLoading.value = true;
      
      try {
        await axios.delete(`/auth/users/${deletingUser.value._id}`);
        await fetchUsers();
        showDeleteModal.value = false;
      } catch (err) {
        console.error('Ошибка при удалении пользователя:', err);
        alert('Не удалось удалить пользователя');
      } finally {
        deleteLoading.value = false;
      }
    };
    
    onMounted(fetchUsers);
    
    return {
      users,
      loading,
      error,
      showAddUserModal,
      showEditUserModal,
      showDeleteModal,
      userForm,
      editingUser,
      deletingUser,
      formLoading,
      formError,
      deleteLoading,
      currentUser,
      showPassword,
      closeModal,
      editUser,
      addUser,
      updateUser,
      confirmDeleteUser,
      deleteUser
    };
  }
};
</script>

<style scoped>
.users-view {
  padding: 20px;
  background-color: #f5f5f5;
  height: 100%;
  overflow-y: auto;
}

.users-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

h2 {
  margin: 0;
  color: #333;
}

.add-user-button {
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.add-user-button i {
  margin-right: 5px;
}

.loading, .error-message {
  text-align: center;
  padding: 20px;
}

.error-message {
  color: #f44336;
}

.users-list {
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  background-color: #f5f5f5;
  font-weight: 500;
  color: #333;
}

.status-active {
  color: #4caf50;
}

.status-inactive {
  color: #f44336;
}

.actions {
  display: flex;
  gap: 10px;
}

.edit-button, .delete-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
}

.edit-button {
  color: #2196f3;
}

.delete-button {
  color: #f44336;
}

.delete-button:disabled {
  color: #bdbdbd;
  cursor: not-allowed;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 5px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #757575;
}

.user-form {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  font-size: 14px;
}

.password-input {
  position: relative;
}

.toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #757575;
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-button, .submit-button, .delete-confirm-button {
  padding: 8px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
}

.cancel-button {
  background-color: #f5f5f5;
  color: #333;
}

.submit-button {
  background-color: #2196f3;
  color: white;
}

.submit-button:disabled {
  background-color: #90caf9;
  cursor: not-allowed;
}

.modal-body {
  padding: 20px;
}

.warning {
  color: #f44336;
  font-weight: 500;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.delete-confirm-button {
  background-color: #f44336;
  color: white;
}

.delete-confirm-button:disabled {
  background-color: #ef9a9a;
  cursor: not-allowed;
}
</style> 